import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Truck, Package, RotateCcw, CheckCircle, AlertTriangle,
  Clock, Phone, MapPin, RefreshCw, ExternalLink, ChevronRight,
  Filter, Calendar
} from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { formatDate } from '../../src/utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const MISSION_TYPES = [
  { key: 'all',          label: 'Toutes',     color: 'var(--admin-text)',     icon: <Filter size={14}/> },
  { key: 'chargement',   label: 'Chargement', color: '#6366F1',  icon: <Package size={14}/> },
  { key: 'livraison',    label: 'Livraison',  color: '#3B82F6',  icon: <Truck size={14}/> },
  { key: 'retour',       label: 'Retour',     color: '#F97316',  icon: <RotateCcw size={14}/> },
];

const STATUT_CONF = {
  en_attente:  { label: 'En attente',   color: '#9CA3AF', bg: 'rgba(156,163,175,0.1)' },
  en_cours:    { label: 'En cours',     color: '#3B82F6', bg: 'rgba(59,130,246,0.1)'  },
  termine:     { label: 'Terminée ✓',   color: '#22C55E', bg: 'rgba(34,197,94,0.1)'   },
  anomalie:    { label: '⚠️ Anomalie',  color: 'var(--admin-red)', bg: 'rgba(239,68,68,0.1)'   },
};

// Statuts commandes qui nécessitent des missions
const STATUTS_ACTIFS = [
  'a_preparer', 'produits_charges', 'en_livraison', 'livree',
  'en_location', 'retour_a_effectuer',
  'payee', 'contrat_signe', 'solde_paye', 'valide_admin', 'confirme',
];

export default function AdminMissions() {
  const [missions, setMissions] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('commandes'); // commandes | missions

  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, qRes] = await Promise.all([
        fetch(`${BACKEND}/api/missions/admin/pending`, { headers: H }).then(r => r.json()),
        fetch(`${BACKEND}/api/devis?limit=100`, { headers: H }).then(r => r.json()),
      ]);
      setMissions(mRes.missions || []);
      const actives = (qRes.quotes || []).filter(q => STATUTS_ACTIFS.includes(q.statut));
      setCommandes(actives);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredMissions = filter === 'all'
    ? missions
    : missions.filter(m => m.type === filter);

  // Grouper les commandes par statut
  const GROUPS = [
    { key: 'a_preparer',        label: '📦 À préparer',         color: 'var(--admin-purple)', statuts: ['a_preparer', 'contrat_signe', 'payee', 'solde_paye', 'valide_admin', 'confirme'] },
    { key: 'en_livraison',      label: '🚚 En livraison',        color: '#3B82F6', statuts: ['produits_charges', 'en_livraison'] },
    { key: 'livree',            label: '✅ Livrée / En location', color: '#22C55E', statuts: ['livree', 'en_location'] },
    { key: 'retour',            label: '↩️ Retour à effectuer',  color: '#F97316', statuts: ['retour_a_effectuer'] },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }} data-testid="admin-missions">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Livraisons</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Missions terrain</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Chargement, livraison et retour</p>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="adm-title"  style={{ color: 'var(--admin-teal)' }}>Missions terrain</h1>
          <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>
            Chargement, livraison et retour du matériel
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm"
            style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}>
            <RefreshCw size={14} className="mr-1" /> Actualiser
          </Button>
          <a href="/mission" target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold"
            style={{ background: 'rgba(59,130,246,0.15)', color: '#3B82F6', border: '1px solid rgba(59,130,246,0.3)', textDecoration: 'none' }}>
            <ExternalLink size={13} /> Ouvrir app terrain
          </a>
        </div>
      </div>

      {/* Toggle vue */}
      <div className="flex rounded-xl overflow-hidden" style={{ border: '1px solid var(--admin-border)', width: 'fit-content' }}>
        {[
          { key: 'commandes', label: `Commandes (${commandes.length})` },
          { key: 'missions',  label: `Missions en cours (${missions.length})` },
        ].map(v => (
          <button key={v.key} onClick={() => setView(v.key)}
            className="px-4 py-2 text-xs font-semibold"
            style={{
              background: view === v.key ? '#5DBCBF' : 'transparent',
              color: view === v.key ? '#0B1E20' : 'rgba(255,255,255,0.5)',
              border: 'none', cursor: 'pointer',
            }}>
            {v.label}
          </button>
        ))}
      </div>

      {/* ── Vue commandes ── */}
      {view === 'commandes' && (
        loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-xl" style={{ background: 'var(--admin-surface)' }} />)}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {GROUPS.map(group => {
              const items = commandes.filter(q => group.statuts.includes(q.statut));
              if (items.length === 0) return null;
              return (
                <div key={group.key}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-bold" style={{ color: group.color }}>{group.label}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-bold"
                      style={{ background: `${group.color}20`, color: group.color }}>
                      {items.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {items.map(q => {
                      const sess = q.session || {};
                      const dateEv = q.date_livraison || sess.date_livraison || sess.date_evenement || '';
                      const heureLiv = q.heure_livraison || sess.heure_livraison || '09:00';
                      return (
                        <div key={q.id} className="rounded-2xl overflow-hidden"
                          style={{ background: 'var(--admin-surface)', border: `1px solid ${group.color}20` }}>
                          <div className="flex items-start gap-3 p-4">
                            {/* Infos client */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <p className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>{q.client_nom}</p>
                                <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                                  style={{ background: `${group.color}20`, color: group.color }}>
                                  {q.statut.replace(/_/g, ' ')}
                                </span>
                              </div>
                              <p className="text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>
                                {q.numero} · {q.total || 0}€
                              </p>
                              {dateEv && (
                                <p className="text-xs flex items-center gap-1.5" style={{ color: 'var(--admin-teal)' }}>
                                  <Calendar size={10} />
                                  {dateEv.slice(0, 10)} à {heureLiv}
                                </p>
                              )}
                              {(q.adresse_livraison || sess.adresse) && (
                                <p className="text-xs mt-1 truncate" style={{ color: 'var(--admin-faint)' }}>
                                  <MapPin size={9} className="inline mr-1" />
                                  {q.adresse_livraison || sess.adresse}
                                </p>
                              )}
                            </div>
                            {/* Actions */}
                            <div className="flex flex-col gap-1.5 flex-shrink-0">
                              {q.client_telephone && (
                                <a href={`tel:${q.client_telephone}`}
                                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                                  style={{ background: 'rgba(93,188,191,0.15)', color: 'var(--admin-teal)', textDecoration: 'none' }}>
                                  <Phone size={13} />
                                </a>
                              )}
                              <a href={`/mission?quote=${q.id}`} target="_blank" rel="noreferrer"
                                className="w-8 h-8 rounded-lg flex items-center justify-center"
                                style={{ background: 'rgba(59,130,246,0.15)', color: '#3B82F6', textDecoration: 'none' }}>
                                <ExternalLink size={13} />
                              </a>
                            </div>
                          </div>

                          {/* Boutons mission rapide */}
                          <div className="flex divide-x" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', borderColor: 'var(--admin-border-soft)' }}>
                            {[
                              { type: 'chargement', label: '📦 Chargement', color: '#6366F1' },
                              { type: 'livraison',  label: '🚚 Livraison',  color: '#3B82F6' },
                              { type: 'retour',     label: '↩️ Retour',     color: '#F97316' },
                            ].map(m => (
                              <a key={m.type}
                                href={`/mission`}
                                target="_blank" rel="noreferrer"
                                className="flex-1 py-2 text-center text-xs font-semibold transition-colors"
                                style={{ color: m.color, textDecoration: 'none', borderColor: 'var(--admin-border-soft)' }}>
                                {m.label}
                              </a>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {commandes.length === 0 && (
              <div className="text-center py-16" style={{ color: 'var(--admin-faint)' }}>
                <CheckCircle size={40} className="mx-auto mb-3 opacity-20" />
                <p>Aucune commande active en ce moment</p>
              </div>
            )}
          </div>
        )
      )}

      {/* ── Vue missions en cours ── */}
      {view === 'missions' && (
        <div>
          {/* Filtres type */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {MISSION_TYPES.map(t => (
              <button key={t.key} onClick={() => setFilter(t.key)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold"
                style={{
                  background: filter === t.key ? t.color : 'rgba(255,255,255,0.05)',
                  color: filter === t.key ? (t.key === 'all' ? '#0B1E20' : '#fff') : 'rgba(255,255,255,0.5)',
                  border: `1px solid ${filter === t.key ? t.color : 'rgba(255,255,255,0.1)'}`,
                  cursor: 'pointer',
                }}>
                {t.icon} {t.label}
                {t.key !== 'all' && (
                  <span className="ml-1 opacity-70">
                    ({missions.filter(m => m.type === t.key).length})
                  </span>
                )}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => <Skeleton key={i} className="h-20 rounded-xl" style={{ background: 'var(--admin-surface)' }} />)}
            </div>
          ) : filteredMissions.length === 0 ? (
            <div className="text-center py-16" style={{ color: 'var(--admin-faint)' }}>
              <Truck size={40} className="mx-auto mb-3 opacity-20" />
              <p>Aucune mission {filter !== 'all' ? filter : ''} en cours</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredMissions.map(m => {
                const typeConf = MISSION_TYPES.find(t => t.key === m.type) || MISSION_TYPES[2];
                const statutConf = STATUT_CONF[m.statut] || STATUT_CONF.en_attente;
                const checked = (m.checklist || []).filter(i => i.checked).length;
                const total = (m.checklist || []).length;
                const pct = total > 0 ? Math.round((checked / total) * 100) : 0;

                return (
                  <div key={m.id} className="rounded-2xl overflow-hidden"
                    style={{ background: 'var(--admin-surface)', border: `1.5px solid ${typeConf.color}30` }}>
                    <div className="flex items-start gap-3 p-4">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: `${typeConf.color}15`, color: typeConf.color }}>
                        {typeConf.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <p className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>{m.client_nom}</p>
                          <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
                            style={{ background: statutConf.bg, color: statutConf.color }}>
                            {statutConf.label}
                          </span>
                          {m.statut === 'anomalie' && (
                            <AlertTriangle size={13} style={{ color: 'var(--admin-red)' }} />
                          )}
                        </div>
                        <p className="text-xs mb-2" style={{ color: 'var(--admin-muted)' }}>
                          {m.quote_numero} · {typeConf.label}
                          {m.date_livraison && ` · ${m.date_livraison}`}
                        </p>
                        {/* Barre progression checklist */}
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }}>
                            <div className="h-full rounded-full transition-all"
                              style={{ width: `${pct}%`, background: pct === 100 ? '#22C55E' : typeConf.color }} />
                          </div>
                          <span className="text-xs font-semibold" style={{ color: 'var(--admin-muted)' }}>
                            {checked}/{total}
                          </span>
                        </div>
                      </div>
                      <a href={`/mission`} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold flex-shrink-0"
                        style={{ background: `${typeConf.color}15`, color: typeConf.color, border: `1px solid ${typeConf.color}30`, textDecoration: 'none' }}>
                        Ouvrir <ExternalLink size={11} />
                      </a>
                    </div>
                    {/* Anomalies */}
                    {m.anomalies?.length > 0 && (
                      <div className="px-4 pb-3 pt-0">
                        {m.anomalies.map((a, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 rounded-lg mb-1"
                            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                            <AlertTriangle size={12} style={{ color: 'var(--admin-red)', flexShrink: 0, marginTop: 1 }} />
                            <p className="text-xs" style={{ color: 'var(--admin-red)' }}>
                              <strong>{a.product_nom}</strong> — {a.description}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
